import { BaseController } from "@repo/baseCloud";
import { PushErrorNotification } from "@repo/helpers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { LayerDefinition, MapConfiguration } from "../models";

import { QUERY_KEYS } from "./queryKeys";

const controller = "MapConfiguration";

interface ServerResult {
	success?: boolean;
	error?: string;
}

export class MapConfigurationController extends BaseController {
	static Get = BaseController.GetFn(controller);
	static Post = BaseController.PostFn(controller);

	/**
	 * Gets all saved map configurations.
	 */
	public static async getAll(): Promise<MapConfiguration[]> {
		const maps = await MapConfigurationController.Get<MapConfiguration[]>("GetAll");
		return maps.map(initializeMap);
	}

	/**
	 * Gets a specific map configuration by ID.
	 */
	public static getById(id: number | null): Promise<MapConfiguration | null> {
		if (id === null) return Promise.resolve(null);
		return MapConfigurationController.Get<MapConfiguration>("GetById", { id }).then(initializeMap);
	}

	/**
	 * Saves (creates or updates) a map configuration.
	 */
	public static async save(config: MapConfiguration): Promise<MapConfiguration> {
		const layers = config.layers.map((l) => ({
			...l,
			configurationJson: JSON.stringify(toLayerConfiguration(l)),
		}));
		const saved = await post<MapConfiguration>("Save", "Failed to save map", {}, { ...config, layers });
		return initializeMap(saved);
	}

	/**
	 * Deletes a map configuration by ID.
	 */
	public static delete(id: number) {
		return post("Delete", "Failed to delete map", { id });
	}

	public static forkConfig(configId: number) {
		return post<{ id: number }>("ForkConfig", "Failed to copy map", { configId });
	}

	public static forkLayer(configId: number, layerId: number) {
		return post<{ id: number; layerId: number }>("ForkLayer", "Failed to copy layer", { configId, layerId });
	}
}

// The server reports failures in a 200 response as { error } or { success: false }.
async function post<T>(action: string, failureMessage: string, params: object, data?: object) {
	const result = await MapConfigurationController.Post<T & ServerResult>(action, params, data).catch(() => undefined);
	if (!result || result.error || result.success === false) throw new Error(result?.error || failureMessage);
	return result;
}

function initializeMap(map: MapConfiguration): MapConfiguration {
	return {
		...map,
		layers: map.layers.map(initializeLayer).toSorted((a, b) => (a?.ordinal ?? 0) - (b?.ordinal ?? 0)),
	};
}

function initializeLayer(layer: LayerDefinition): LayerDefinition {
	if (!layer) return layer;

	let configuration: Partial<LayerDefinition> = {};
	try {
		const parsed = JSON.parse(layer.configurationJson ?? "{}");
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			configuration = removeLayerMetadata(parsed as Partial<LayerDefinition>);
		}
	} catch {
		// Keep the layer usable when an older/invalid configuration is returned.
	}

	return {
		...configuration,
		...layer,
	};
}

const LAYER_METADATA_KEYS = [
	"id",
	"layerId",
	"name",
	"visible",
	"itemType",
	"color",
	"icon",
	"lastPositions",
	"ordinal",
	"configurationJson",
	"items",
	"isLoading",
	"totalCount",
	"executeCount",
	"executedFilters",
] as const;

function removeLayerMetadata(layer: Partial<LayerDefinition>): Partial<LayerDefinition> {
	const configuration = { ...layer } as Record<string, unknown>;
	for (const key of LAYER_METADATA_KEYS) delete configuration[key];
	return configuration as Partial<LayerDefinition>;
}

export function toLayerConfiguration(layer: LayerDefinition): Partial<LayerDefinition> {
	return removeLayerMetadata(layer);
}
/**
 * Hook to get all map configurations.
 */
export function useMapConfigurations() {
	return useQuery({
		queryKey: [QUERY_KEYS.Maps.MapConfigurations],
		queryFn: () => MapConfigurationController.getAll(),
	});
}

/**
 * Hook to get a specific map configuration.
 */
export function useMapConfiguration(id: number | null) {
	return useQuery({
		queryKey: [QUERY_KEYS.Maps.MapConfigurations, id],
		queryFn: () => MapConfigurationController.getById(id),
		enabled: id !== null,
	});
}

function useMapConfigurationMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.Maps.MapConfigurations] }),
		onError: (error: Error) => PushErrorNotification(error.message),
	});
}

export const useSaveMapConfiguration = () =>
	useMapConfigurationMutation((config: MapConfiguration) => MapConfigurationController.save(config));

export const useDeleteMapConfiguration = () =>
	useMapConfigurationMutation((id: number) => MapConfigurationController.delete(id));

export const useForkConfig = () =>
	useMapConfigurationMutation((configId: number) => MapConfigurationController.forkConfig(configId));

export const useForkLayer = () =>
	useMapConfigurationMutation((params: { configId: number; layerId: number }) =>
		MapConfigurationController.forkLayer(params.configId, params.layerId),
	);
